<script>
    // One verdict for many disputed rows (869f2xwhc): tick rows on this page,
    // then one action. 869fcmxu1 dropped the game / model selects and the
    // "all disputes" scope (Dasha: an unclear helper) and added "Свой тип" for
    // rows where neither the folder nor the model was right.
    import { resolveDisputes } from '@/api/file.js';
    import { findFilterGroupByName } from '@/scripts/sortFilesByAssetType.mjs';
    import constants from '@/constants/index.js';
    import OwnTypePicker from './OwnTypePicker.vue';
    import DisputeFilter from './DisputeFilter.vue';

    export const TIPS = {
        check: 'Выбрать для массового действия.',
        preview: 'Открыть оригинал.',
        folder: 'Оставить тип из папки на Диске. Спор закрыт, строка уходит из списка.',
        model: 'Поставить тип, который предложила модель, вместо типа папки. Спор закрыт.',
        del: 'Удалить файл навсегда - из каталога и из S3. Портал его больше не загрузит. Отменить нельзя.',
        props: 'Раскрыть полную карточку: фильтры, теги, HQ, статус.',
        page: 'Отметить все строки на этой странице. Повторный клик снимает отметки.',
    };

    export default {
        components: { OwnTypePicker, DisputeFilter },
        data() {
            return { busy: false, busyText: '', confirmDelete: null, stuck: false, TIPS };
        },
        computed: {
            images() { return this.$store.getters.IMAGES },
            selectedIds() { return this.$store.getters.SELECTED_IDS },
            assetTypeID() { return this.$store.getters.ASSET_TYPE_IMAGES },
            types() {
                const schema = this.$store.getters.FILTER_BY_ASSET_TYPE(this.assetTypeID);
                const group = findFilterGroupByName(schema, constants.ASSET_TYPE_FILTER_NAME);
                return (group?.filter_items ?? [])
                    .map((i) => ({ id: i.id, name: i.name }))
                    .sort((a, b) => a.name.localeCompare(b.name));
            },
            allTicked() {
                return this.images.length > 0 && this.images.every((f) => this.selectedIds.includes(f.id));
            },
            canAct() { return !this.busy && this.selectedIds.length > 0 },
            actHint() {
                if (this.busy) return '';
                if (!this.canAct) return 'Отметьте строки галочками';
                return `Применится к ${this.selectedIds.length} строкам`;
            },
            bulkSuffix() { return this.canAct ? ` Для всех выбранных (${this.selectedIds.length}).` : '' },
        },
        mounted() {
            // 869faf4n4: once the full bar scrolls above the viewport a compact
            // copy pins to the top, so rows ticked far down can be acted on.
            this.observer = new IntersectionObserver(([e]) => {
                this.stuck = !e.isIntersecting && e.boundingClientRect.top < 0;
            });
            this.observer.observe(this.$el);
        },
        beforeUnmount() { this.observer.disconnect(); },
        methods: {
            togglePage() {
                if (this.allTicked) this.$store.commit('CLEAR_SELECTED');
                else this.$store.commit('SET_SELECTED', this.images.map((f) => f.id));
            },
            askDelete() {
                if (!this.canAct) return;
                this.confirmDelete = `Удалятся ${this.selectedIds.length} отмеченных строк на этой странице.`;
            },
            async act(action, type = null) {
                if (!this.canAct) return;
                this.confirmDelete = null;
                this.busy = true;
                this.busyText = {
                    keep_folder: 'Применяю тип папки…',
                    keep_model: 'Применяю тип модели…',
                    own_type: `Ставлю тип ${type?.name}…`,
                    delete: 'Удаляю…',
                }[action];
                const ids = [...this.selectedIds];
                this.$store.commit('SET_BUSY', ids);
                try {
                    const body = { action, ids };
                    if (type) body.asset_type_item_id = type.id;
                    const out = await resolveDisputes(this.assetTypeID, body);
                    if (out == null) return;
                    (out.ids || []).forEach((id) => this.$store.commit('IMAGE_DELETE_BY_ID', id));
                    this.$store.commit('CLEAR_SELECTED');
                    this.$refs.own?.reset();
                    this.$refs.ownMini?.reset();
                    // Refetch so the header count and the page come from the server.
                    this.$store.commit('IS_LOAD', true);
                    await this.$store.dispatch('UPDATE_FILES');
                    this.$store.commit('IS_LOAD', false);
                } finally {
                    this.$store.commit('SET_BUSY', []);
                    this.busy = false;
                }
            },
        },
    };
</script>

<template>
    <div class="bulk">
        <DisputeFilter />
        <div class="bulk_top">
            <span class="bulk_label">Выбрано</span>
            <span class="bulk_count" :class="{ zero: !canAct }">{{ selectedIds.length }}</span>
            <span class="bulk_sub">на этой странице</span>
            <button class="btn_outline" :title="TIPS.page" @click="togglePage">{{ allTicked ? 'Снять все на странице' : 'Все на странице' }}</button>
        </div>
        <div class="bulk_actions">
            <span class="bulk_label">Что сделать</span>
            <button class="button_blue_150" :disabled="!canAct" :title="TIPS.folder + bulkSuffix" @click="act('keep_folder')">Папка права</button>
            <button class="button_blue_150" :disabled="!canAct" :title="TIPS.model + bulkSuffix" @click="act('keep_model')">Модель права</button>
            <OwnTypePicker ref="own" :types="types" :disabled="!canAct" @apply="(t) => act('own_type', t)" />
            <button class="button_blue_150 button_red" :disabled="!canAct" :title="TIPS.del + bulkSuffix" @click="askDelete">Удалить</button>
            <span class="bulk_sub">{{ actHint }}</span>
            <span v-if="busy" class="bulk_busy"><span class="spin"></span>{{ busyText }}</span>
        </div>

        <div v-if="stuck" class="bulk_mini">
            <span class="bulk_label">Выбрано</span>
            <span class="bulk_count mini_count" :class="{ zero: !canAct }">{{ selectedIds.length }}</span>
            <span class="bulk_sub mini_hint">{{ busy ? busyText : actHint }}</span>
            <button class="button_blue_150" :disabled="!canAct" :title="TIPS.folder + bulkSuffix" @click="act('keep_folder')">Папка права</button>
            <button class="button_blue_150" :disabled="!canAct" :title="TIPS.model + bulkSuffix" @click="act('keep_model')">Модель права</button>
            <OwnTypePicker ref="ownMini" :types="types" :disabled="!canAct" @apply="(t) => act('own_type', t)" />
            <button class="button_blue_150 button_red" :disabled="!canAct" :title="TIPS.del + bulkSuffix" @click="askDelete">Удалить</button>
        </div>

        <div v-if="confirmDelete" class="modal_backdrop" @click.self="confirmDelete = null">
            <div class="modal">
                <div class="modal_title">Удалить {{ selectedIds.length }} файлов навсегда?</div>
                <div class="modal_body">{{ confirmDelete }}</div>
                <div class="bulk_warn modal_warn">Файлы уйдут из каталога и из S3. Портал их больше не загрузит. Отменить нельзя.</div>
                <div class="modal_buttons">
                    <button class="btn_plain" @click="confirmDelete = null">Отмена</button>
                    <button class="button_blue_150 button_red" @click="act('delete')">Удалить</button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .bulk_top { display: flex; align-items: center; gap: 10px; padding: 14px 20px; border-bottom: 1px solid var(--background-hover); }
    .bulk_top .btn_outline { margin-left: 14px; }
    .bulk_top .bulk_count, .bulk_top .bulk_sub { margin-top: 0; }
    .bulk {
        margin: 0 12px 12px;
        background: var(--background-color);
        border: 1px solid var(--border-color);
        border-radius: 5px;
        color: var(--text-color);
    }
    .bulk_label {
        font-size: 11px;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--text-color-secondary);
        font-weight: bold;
    }
    .bulk_count { margin-top: 6px; font-size: 20px; font-weight: bold; color: var(--main-color); }
    .bulk_count.zero { color: var(--text-color-secondary); }
    .bulk_sub { margin-top: 4px; font-size: 12px; color: var(--text-color-secondary); line-height: 1.4; }
    .btn_outline, .btn_plain {
        font: inherit;
        font-size: 13px;
        border-radius: 5px;
        padding: 8px 12px;
        cursor: pointer;
        background: var(--background-color);
        color: var(--text-color);
        border: 1px solid var(--border-color);
    }
    .btn_outline { font-weight: bold; color: var(--main-color); border-color: var(--main-color); }
    .btn_outline:hover, .btn_plain:hover { background: var(--background-hover); }
    .bulk_warn {
        display: flex;
        align-items: center;
        gap: 12px;
        background: #ffab00;
        color: #292929;
        border-top: 1px solid var(--border-color);
        padding: 12px 20px;
        font-size: 13px;
    }
    .bulk_actions {
        display: flex; flex-wrap: wrap; align-items: center; gap: 10px;
        background: var(--background-secondary);
        border-top: 1px solid var(--border-color);
        padding: 14px 20px;
    }
    .bulk_actions .bulk_label { margin-right: 6px; }
    .bulk_actions .bulk_sub { margin-top: 0; }
    .bulk_actions .button_blue_150 { height: 36px; }
    .bulk_actions .button_blue_150:disabled { opacity: 0.45; }
    .bulk_busy { display: flex; align-items: center; gap: 8px; margin-left: auto; font-size: 13px; font-weight: bold; color: var(--main-color); }
    .spin {
        width: 14px; height: 14px;
        border: 2px solid var(--main-color); border-top-color: transparent;
        border-radius: 50%;
        animation: bulk_spin 0.8s linear infinite;
    }
    @keyframes bulk_spin { to { transform: rotate(360deg); } }
    /* Left panel is fixed at 200px; 16px matches the right container gutter. */
    .bulk_mini {
        position: fixed; top: 0; left: 216px; right: 16px; z-index: 50;
        display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
        background: var(--background-color);
        border: 1px solid var(--border-color); border-top: 0;
        border-radius: 0 0 5px 5px;
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        padding: 10px 20px;
    }
    .bulk_mini .button_blue_150 { height: 36px; }
    .bulk_mini .button_blue_150:disabled { opacity: 0.45; }
    .mini_count { margin-top: 0; }
    .mini_hint { margin: 0 auto 0 0; }
    .modal_backdrop {
        position: fixed; inset: 0; z-index: 100;
        background: rgba(0, 0, 0, 0.45);
        display: flex; align-items: center; justify-content: center; padding: 24px;
    }
    .modal {
        background: var(--background-color); color: var(--text-color);
        border-radius: 5px; padding: 24px 28px; width: 520px; max-width: 100%;
        box-shadow: 0 16px 40px rgba(0, 0, 0, 0.3);
    }
    .modal_title { font-size: 16px; font-weight: bold; }
    .modal_body { margin-top: 12px; font-size: 13px; line-height: 1.55; }
    .modal_warn { margin-top: 14px; border: 0; border-radius: 5px; padding: 10px 12px; font-weight: bold; }
    .modal_buttons { margin-top: 20px; display: flex; justify-content: flex-end; gap: 10px; }
</style>
